/**
 * Helpers for the cross-origin OAuth handoff used by the Bobbin /
 * CLI sign-in flow.
 *
 * Why this exists:
 *
 * Bobbin (and the older CLI flow) opens
 *   ``https://thread.dev-simulation.cloud/thread/connect/bobbin?…``
 * even though Keycloak only accepts ``thread.simulation.cloud`` as a
 * redirect origin. When the user clicks "Sign in with Google" on dev,
 * ``signInWithOAuth`` notices the mismatch and hands off cross-origin
 * to prod with a ``?thread_auth_provider=…`` param. On prod, ``App``
 * picks that param up and re-triggers ``signInWithOAuth`` with a
 * ``redirectTo`` that points back at ``/auth/callback?next=…``. After
 * Keycloak, AuthCallback bounces the browser via
 * ``window.location.assign(next)`` (for paths matching
 * ``CLI_RETURN_SEGMENTS``).
 *
 * The trap: ``useLocation().pathname`` returns the path with the
 * ``BrowserRouter`` basename (``/thread``) already stripped, so a
 * naive ``next = location.pathname + search`` ends up as
 * ``/connect/bobbin?…`` instead of ``/thread/connect/bobbin?…``.
 * ``window.location.assign`` is not router-aware — it just navigates
 * the browser — and the resulting URL is outside the basename, so
 * React Router renders the home page instead of the Bobbin connect
 * screen. The user then has to manually re-trigger the flow.
 *
 * This helper re-attaches the basename to both ``next`` and
 * ``redirectTo`` so the round-trip lands on the correct path.
 */

export interface OAuthHandoffInput {
  /** ``location.pathname`` from ``useLocation`` — basename stripped. */
  pathname: string;
  /** ``location.search`` from ``useLocation``, including the leading ``?``. */
  search: string;
  /** ``window.location.origin`` of the current document. */
  origin: string;
  /** ``import.meta.env.BASE_URL`` — has a trailing slash in prod. */
  baseUrl: string;
}

export interface OAuthHandoffUrls {
  /** Post-auth destination, including the basename. */
  next: string;
  /** Absolute URL Keycloak should redirect back to. */
  redirectTo: string;
}

/**
 * Build ``{ next, redirectTo }`` for the cross-origin OAuth handoff.
 *
 * Both values include the BrowserRouter basename so the post-auth
 * bounce-back lands on a route the router can match.
 */
export function buildOAuthHandoffUrls(args: OAuthHandoffInput): OAuthHandoffUrls {
  const params = new URLSearchParams(args.search);
  params.delete("thread_auth_provider");
  const query = params.toString();
  const base = args.baseUrl.replace(/\/$/, "");
  const next = `${base}${args.pathname}${query ? `?${query}` : ""}`;
  const redirectTo = `${args.origin}${base}/auth/callback?next=${encodeURIComponent(next)}`;
  return { next, redirectTo };
}
